import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useInView } from "../../util/useInView";
import type { CaseCard, CaseGroup as Group } from "./CasesContent";

const pad = (n: number) => String(n).padStart(2, "0");

function Card({ card, index }: { card: CaseCard; index: number }) {
  const { t } = useTranslation();
  const classes = `cz-area-card${card.highlight ? " cz-area-card--highlight" : ""}${card.to ? " cz-area-card--link" : ""}`;
  const body = (
    <>
      <div className="cz-area-card__top">
        <span className="cz-area-card__icon" aria-hidden="true">
          <card.Icon size={22} strokeWidth={1.7} />
        </span>
        {card.to && (
          <span className="cz-area-card__go" aria-hidden="true">
            <ArrowUpRight size={16} />
          </span>
        )}
      </div>
      {card.tag && <span className="cz-area-card__tag">{card.tag}</span>}
      <h3 className="cz-area-card__title">{card.title}</h3>
      <ul className="cz-area-card__points">
        {card.bullets.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
      {card.to && (
        <span className="cz-area-card__more">
          {t("cases_page.labels.see_detail")}
          <ArrowRight size={15} aria-hidden="true" />
        </span>
      )}
    </>
  );

  return (
    <li className="cz-area__cell" data-reveal style={{ "--i": index } as React.CSSProperties}>
      {card.to ? (
        <Link to={card.to} className={classes}>
          {body}
        </Link>
      ) : (
        <div className={classes}>{body}</div>
      )}
    </li>
  );
}

// Cases por área: coluna fixa à esquerda (número, selo, título, texto e quantos cases) e os cards à direita, que
// entram em cascata. No layout "split" (Moodle, rede e segurança) os itens de segurança vêm em linhas abaixo dos cards.
// band = fundo em faixa clara (as seções alternam com o branco).
export default function CaseGroup({ group, index, band }: { group: Group; index: number; band?: boolean }) {
  const { t } = useTranslation();
  const titleId = `${group.id}-title`;
  const [ref, state] = useInView<HTMLDivElement>(0.12);
  const count = group.cards.length + (group.items?.length ?? 0);

  return (
    <section
      className={`cz-cases-section${band ? " cz-cases-section--band" : ""}`}
      id={group.id}
      aria-labelledby={titleId}
    >
      <div className="cz-cases-section__inner cz-area">
        <div className="cz-area__intro">
          <div className="cz-area__marker" aria-hidden="true">
            <span className="cz-area__badge">
              <group.Icon size={26} strokeWidth={1.6} />
            </span>
            <span className="cz-outline-num cz-area__num">{pad(index + 1)}</span>
          </div>
          <span className="cz-eyebrow">{t("cases_page.labels.area_eyebrow", "Cases por área")}</span>
          <h2 className="cz-cases-title" id={titleId}>
            {group.title}
          </h2>
          <p className="cz-cases-subtitle">{group.text}</p>
          <span className="cz-area__count">
            {t("cases_page.labels.cases_count", { count, defaultValue: "{{count}} cases" })}
          </span>
        </div>

        <div ref={ref} className={`cz-area__content${state}`}>
          {group.layout === "split" && <span className="cz-case-label cz-area__label">{group.leftLabel}</span>}
          <ul className="cz-area__cards">
            {group.cards.map((card, i) => (
              <Card key={card.key} card={card} index={i} />
            ))}
          </ul>

          {group.layout === "split" && group.items && (
            <>
              <span className="cz-case-label cz-area__label">{group.rightLabel}</span>
              <ul className="cz-area__rows">
                {group.items.map(({ key, Icon, title, text }, i) => (
                  <li
                    key={key}
                    className="cz-area__row"
                    data-reveal
                    style={{ "--i": group.cards.length + i } as React.CSSProperties}
                  >
                    <span className="cz-area__row-icon" aria-hidden="true">
                      <Icon size={20} strokeWidth={1.7} />
                    </span>
                    <span className="cz-area__row-text">
                      <strong>{title}</strong>
                      {text}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
